import React, { useEffect, useRef } from 'react';
import type {
  CalendarApi,
  DateSelectArg,
  DatesSetArg,
  EventApi,
  EventContentArg,
  EventMountArg,
  EventSourceInput,
  FormatterInput,
} from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import momentPlugin from '@fullcalendar/moment';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import styled, { createGlobalStyle } from 'styled-components';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { bodyStyles } from '@grnhse/seedling/lib/birch/internal/internal-typography';
import type {
  CalendarEvent,
  CalendarEditMode,
  UserDateFormat,
  UserTimeFormat,
} from '../types';
import uuid from 'uuid';
import { withLocalePrefix } from '../../../shared/utils/translation';
import { FullCalendar } from 'full_calendar/full_calendar';

const t = withLocalePrefix('candidate_profile_redesign.interviews');

type AvailabilityCalendarProps = {
  mode: CalendarEditMode;
  timeZoneLabel: string;
  timezone?: string;
  value: Array<CalendarEvent>;
  handleSetApi?: (api: CalendarApi) => void;
  handleRemoveEvents?: (eventId: number) => void;
  handleUserCreatedEventsChange?: (events: Array<CalendarEvent>) => void;
  onChange: (e: Array<CalendarEvent>) => void;
  onDateChange?: (e: DatesSetArg) => void;
  timeFormat?: UserTimeFormat;
  dateFormat?: UserDateFormat;
};

const AvailabilityCalendar = ({
  mode,
  timeZoneLabel,
  timezone,
  value,
  handleSetApi,
  handleRemoveEvents,
  onChange,
  onDateChange,
  timeFormat = 'h:mma',
  dateFormat = 'MMM D, YYYY',
  handleUserCreatedEventsChange,
}: AvailabilityCalendarProps) => {
  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    if (!calendarRef?.current) {
      return;
    }

    const calendarApi = calendarRef.current.getApi();
    if (handleSetApi) {
      handleSetApi(calendarApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Add timezone label to title bar
   * @returns
   */
  const onCalendarLoad = () => {
    if (!timeZoneLabel) {
      return;
    }

    const existingLabel = document.querySelector('#availability_time_zone');
    if (existingLabel) {
      existingLabel.textContent = timeZoneLabel;
      return;
    }

    const newLabel = document.createElement('span');
    newLabel.setAttribute('id', 'availability_time_zone');
    newLabel.textContent = timeZoneLabel;

    const container = document.querySelector('.fc-toolbar-chunk:nth-child(2)');
    if (container) {
      container.appendChild(newLabel);
    }
    return;
  };

  const convertEventApiToCalendarEvent = (
    updatedEvents: Array<EventApi>
  ): CalendarEvent[] => {
    return updatedEvents.map((e: EventApi) => {
      return {
        allDay: e.allDay,
        start: e.startStr,
        end: e.endStr,
        greenhouse_type: e.extendedProps.greenhouse_type ?? 'candidate',
      };
    });
  };

  const handleEventsChange = (updatedEvents: Array<EventApi>) => {
    const eventValues = convertEventApiToCalendarEvent(updatedEvents);
    onChange(eventValues);

    if (handleUserCreatedEventsChange) {
      const userCreatedEvents = eventValues.filter(
        (event) => event.greenhouse_type === 'candidate'
      );
      handleUserCreatedEventsChange(userCreatedEvents);
    }
  };

  const handleDateChange = (view: DatesSetArg) => {
    if (onDateChange) {
      onDateChange(view);
    }
  };

  const handleAddEvent = (selectedEvent: DateSelectArg) => {
    const calendarApi = selectedEvent.view.calendar;

    const event = {
      id: uuid.v4(),
      start: selectedEvent.startStr,
      end: selectedEvent.endStr,
    };
    calendarApi.unselect();
    calendarApi.addEvent(event);
  };

  const checkEventType = (
    ev: EventContentArg | EventContentArg,
    type: CalendarEvent['greenhouse_type']
  ) => {
    return ev.event.extendedProps.greenhouse_type === type;
  };

  const isProposedTime = (ev: EventContentArg | EventContentArg) => {
    return checkEventType(ev, 'proposed');
  };

  const isInterviewerTime = (ev: EventMountArg) => {
    return checkEventType(ev, 'interviewer');
  };

  const formatAs24HourTime = timeFormat === 'H:mm';

  const formattingOptions: FormatterInput = {
    hour: 'numeric',
    minute: 'numeric',
    meridiem: formatAs24HourTime ? false : 'short',
    hour12: formatAs24HourTime ? false : true,
    omitZeroMinute: !formatAs24HourTime,
  };

  const getUserTitleFormat = (format: UserDateFormat) => {
    switch (format) {
      case 'D MMM YYYY':
        return '{{D} MMM} YYYY';
      case 'MMM D, YYYY':
        return '{MMM {D}}, YYYY';
      case 'YYYY MMM D':
        return 'YYYY {MMM {D}}';
      default:
        return '{MMM {D}}, YYYY';
    }
  };

  const userDayHeaderFormat = `ddd ${dateFormat
    .replace('YYYY', '')
    .replace(',', '')}`;
  const userTitleFormat = getUserTitleFormat(dateFormat);

  /**
   * Add remove button to event
   * @param {*} eventInfo
   */
  const addRemoveButton = (eventInfo: EventMountArg) => {
    const removeButton = document.createElement('div');
    removeButton.classList.add('remove-button');
    removeButton.title = 'Remove';

    const span = document.createElement('span');
    span.textContent = 'X';

    removeButton.append(span);
    removeButton.addEventListener('click', () => {
      eventInfo.event.remove();
      if (handleRemoveEvents) {
        handleRemoveEvents(Number(eventInfo.event.id));
      }
    });
    eventInfo.el.prepend(removeButton);
  };

  /**
   * Adjust styling of proposed events
   * @param {*} eventInfo
   */
  const setEventAsProposed = (eventInfo: EventMountArg) => {
    eventInfo.event.setProp('textColor', FullPalette.EVERGREEN_600);
    eventInfo.event.setProp('classNames', 'proposed');
    eventInfo.event.setProp('display', 'background');
  };

  /**
   * Add remove button and style proposed events
   * @param {*} eventInfo
   */
  const handleEventMount = (eventInfo: EventMountArg) => {
    if (isProposedTime(eventInfo) && mode !== 'edit_proposed') {
      setEventAsProposed(eventInfo);
      return;
    } else if (isInterviewerTime(eventInfo)) {
      setEventAsProposed(eventInfo);
      return;
    }
    addRemoveButton(eventInfo);
  };

  /**
   * Add "suggested time" label to proposed events
   * @param {*} eventInfo
   * @returns
   */
  const renderEvent = (eventInfo: EventContentArg) => {
    if (!eventInfo?.event?.extendedProps?.greenhouse_type) {
      return true; // Render default content
    }

    if (isProposedTime(eventInfo) && mode === 'edit_candidate') {
      return (
        <CenterText>
          <StyledSecondaryBody>{t('suggested_time')}</StyledSecondaryBody>
        </CenterText>
      );
    }

    return true; // Render default content
  };

  return (
    <>
      <GlobalStyle />
      <FullCalendar
        plugins={[
          timeGridPlugin,
          interactionPlugin,
          momentTimezonePlugin,
          momentPlugin,
        ]}
        headerToolbar={{
          left: 'title',
          center: '',
          right: 'prev,next,today',
        }}
        initialView="timeGridWeek"
        views={{
          timeGrid: {
            titleFormat: userTitleFormat,
            slotLabelFormat: formattingOptions,
            dayHeaderFormat: userDayHeaderFormat,
          },
        }}
        eventTimeFormat={formattingOptions}
        allDaySlot={false}
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={false}
        weekends={true}
        ref={calendarRef}
        viewClassNames={() => {
          onCalendarLoad();
          return [];
        }}
        datesSet={handleDateChange}
        displayEventTime
        eventsSet={handleEventsChange}
        eventDidMount={handleEventMount}
        events={value as EventSourceInput}
        select={handleAddEvent}
        eventContent={renderEvent}
        eventColor={FullPalette.EVERGREEN_600}
        eventBorderColor={FullPalette.EVERGREEN_600}
        timeZone={timezone}
        eventClassNames={'gh-cal-events pii-blur'}
      />
    </>
  );
};

export default AvailabilityCalendar;

const CenterText = styled.div`
  text-align: center;
  margin-top: 2px;
`;

const StyledSecondaryBody = styled(SecondaryBody)`
  color: ${FullPalette.EVERGREEN_600};
`;

const GlobalStyle = createGlobalStyle`

  .fc-theme-standard{
    font-family: 'Untitled Sans', sans-serif;
    color: ${FullPalette.EVERGREEN_700} !important;
  }
  .fc-button {
    font-size: 1rem;
    line-height: 1.5rem;
    padding: 0.5rem 1.5rem;
    background-color: ${FullPalette.WHITE} !important;
    border: solid 1px ${FullPalette.GREEN_700} !important;
    color: ${FullPalette.GREEN_700} !important;
    font-family: 'Untitled Sans', sans-serif;
  }
  .fc-prev-button,
  .fc-next-button {
    border: none !important;
  }

  .proposed, .fc-interviewer-event {
    border: 1px dashed ${FullPalette.EVERGREEN_E200} !important;
    background-color: ${FullPalette.EVERGREEN_E50} !important;
    color: ${FullPalette.EVERGREEN_600} !important;
  }

  .fc-event-time {
    margin-left: 3px;
    text-wrap: wrap !important;
  }

  .gh-cal-events .fc-event-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 1px !important;
    line-height: 1.2;
    max-height: 1.2em;
  }

  .fc-bg-event {
    opacity: unset !important;
    border-radius: 2px !important;
  }

  .fc-toolbar-title{
    font-size: 16px !important;
    font-weight: 500;
  }

  .fc-timegrid-event-harness-inset .fc-timegrid-event{
    box-shadow: unset !important;
  }

  .fc-col-header-cell-cushion {
    font-weight: 400;
  }

  .fc-today-button {
    padding-left: 16px !important;
    padding-right: 16px !important;
    border-radius: 8px !important;
    margin-left: 8px !important;
    text-transform: capitalize !important;

    &:hover:enabled {
      color: ${FullPalette.EVERGREEN_700} !important;
      border-color: ${FullPalette.EVERGREEN_700} !important;
      background-color: ${FullPalette.WHITE} !important;
    }
  }

  .remove-button{
    top: 0px;
    right: 2px;
    position: absolute;
    width: 18px;
    height: 18px;
    z-index: 1000;

    &:hover{
      background-color: ${FullPalette.IRIS_E50};
      border-radius: 15px;

      span{
        color: ${FullPalette.EVERGREEN_700} !important;
      }
    }

    span{
      cursor: pointer;
      color: ${FullPalette.WHITE};
      display: flex;
      justify-content: center;
      line-height: 18px;
    }
  }

  #availability_time_zone{
    margin-left: 0;
    ${bodyStyles};
    color: ${FullPalette.GREY_900};
    font-size: 14px;
  }
`;
